import { Component, Input, OnInit } from "@angular/core";
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { BackendApiService } from "../../../../../app/services/backend-api.service";
import { TrendsChartComponent } from "../../trends-chart/trends-chart.component";
import { BaseUrlConstantsService } from "../../../../../app/services/base-url-constants.service";

interface ChartDataPoint {
  x: Date | number;
  y: number;
}

interface ChartConfig {
  loading: boolean;
  data: any;
  dataSeries: any[];
  thresholds: { green?: number; yellow?: number; red?: number };
  diagnosticMessage?: string;
}

type TimeFilter = 'hourly' | 'daily' | 'weekly';

@Component({
  selector: 'app-bearing-analysis',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    TrendsChartComponent
  ],
  templateUrl: './bearing-analysis.component.html',
  styleUrl: './bearing-analysis.component.scss'
})
export class BearingAnalysisComponent implements OnInit {
  @Input() data: any;
  public clientId: string | null = null;
  public selectedTimeFilter: TimeFilter = 'hourly';

  // State object
  public charts: {
    autoRegression: ChartConfig;
    envelope: ChartConfig;
  } = {
    autoRegression: { loading: false, data: null, dataSeries: [], thresholds: {} },
    envelope: { loading: false, data: null, dataSeries: [], thresholds: {} }
  };

  constructor(private service: BackendApiService) {
    this.clientId = localStorage.getItem('user_id');
  }

  ngOnInit(): void {
    if (this.data) {
      this.fetchAutoRegression();
      this.fetchEnvelopeAnalysis();
    }
  }

  changeTimeFilter(filter: TimeFilter) {
    if (this.selectedTimeFilter === filter) return;
    this.selectedTimeFilter = filter;
    this.fetchAutoRegression();
  }

  private getApiTimeParam(): string {
    switch (this.selectedTimeFilter) {
      case 'daily': return 'day';
      case 'weekly': return 'week';
      case 'hourly': default: return 'hour';
    }
  }

  // --- 1. Auto Regression (Time Domain) ---
  async fetchAutoRegression() {
    this.charts.autoRegression.loading = true;
    try {
      const timeParam = this.getApiTimeParam();
      const apiUrl = `${BaseUrlConstantsService.PIEZO_DIAGNOSTICS_BEARING_ANALYSIS_GRAPH_API}/${this.clientId}/${this.data.inventoryId}/${this.data.assetId}/${timeParam}`;

      const response: any = await this.service.getDataV2(apiUrl);
      const body = response?.data?.body || response?.body || response;

      if (body) {
        this.charts.autoRegression.data = body;
        this.charts.autoRegression.thresholds = {
          green: body.green,
          yellow: body.yellow,
          red: body.red
        };
        // Generate Series
        this.charts.autoRegression.dataSeries = this.createTimeSeries(body);
      }
    } catch (error) {
      console.error('Error fetching Auto Regression:', error);
    } finally {
      this.charts.autoRegression.loading = false;
    }
  }

  // --- 2. Envelope Analysis (Frequency Domain) ---
  async fetchEnvelopeAnalysis() {
    this.charts.envelope.loading = true;
    try {
      const apiUrl = `${BaseUrlConstantsService.PIEZO_DIAGNOSTICS_ENVELOP_ANALYSIS_CHART_API}${this.data.latestKurtosisId}`;

      const response: any = await this.service.getDataV2(apiUrl);
      const body = response?.data?.plottingData;

      if (body) {
        this.charts.envelope.data = body;
        this.charts.envelope.diagnosticMessage = body.errorMessage;
        // Generate Series
        this.charts.envelope.dataSeries = this.createFrequencySeries(body);
      }
    } catch (error) {
      console.error('Error fetching Envelope Analysis:', error);
    } finally {
      this.charts.envelope.loading = false;
    }
  }

  // --- Helpers ---

  private parseDatePoints(data: any[]): ChartDataPoint[] {
    if (!Array.isArray(data)) return [];
    return data.map(point => ({
      x: new Date(point.x),
      y: typeof point.y === 'number' ? point.y : parseFloat(point.y)
    }));
  }

  private parseFrequencyPoints(data: any[]): ChartDataPoint[] {
    if (!Array.isArray(data)) return [];
    // IMPORTANT: Keep x as pure number, do NOT convert to Date
    return data.map(point => ({
      x: typeof point.x === 'number' ? point.x : parseFloat(point.x),
      y: typeof point.y === 'number' ? point.y : parseFloat(point.y)
    }));
  }

  // Builder for Time-based Chart (Dates)
  createTimeSeries(data: any): any[] {
    const { chartXData, chartYData, chartZData, nChartArrX, nChartArrY, nChartArrZ, labelx, labely, labelz } = data;
    const series: any[] = [];

    const add = (arr: any[], name: string, color: string, isPred: boolean) => {
      if (arr?.length) {
        series.push({
          type: "line",
          name: name + (isPred ? " (Predicted)" : ""),
          showInLegend: true,
          xValueType: "dateTime",
          lineDashType: isPred ? "dash" : "solid",
          xValueFormatString: "DD MMM HH:mm",
          yValueFormatString: "#0.#####",
          dataPoints: this.parseDatePoints(arr),
          color: color,
          lineThickness: 2,
          markerSize: isPred ? 0 : 4
        });
      }
    };

    add(chartXData, labelx || "Radial", "#008FFB", false);
    add(chartYData, labely || "Tangential", "#00E396", false);
    add(chartZData, labelz || "Axial", "#FEB019", false);
    add(nChartArrX, labelx || "Radial", "#008FFB", true);
    add(nChartArrY, labely || "Tangential", "#00E396", true);
    add(nChartArrZ, labelz || "Axial", "#FEB019", true);

    return series;
  }

  // Builder for Frequency-based Chart (Numbers)
  createFrequencySeries(data: any): any[] {
    const { chartXData, chartYData, chartZData } = data;
    const series: any[] = [];

    const add = (arr: any[], name: string, color: string) => {
      if (arr?.length) {
        // Ensure data points are properly parsed as numbers
        const dataPoints = this.parseFrequencyPoints(arr);

        series.push({
          type: "line",
          name: name,
          showInLegend: true,
          xValueType: "number", // CRITICAL: This tells CanvasJS it's numeric data
          xValueFormatString: "#,##0.## Hz",
          yValueFormatString: "#0.#######",
          dataPoints: dataPoints,
          color: color,
          lineThickness: 2,
          markerSize: 0
        });
      }
    };

    add(chartXData, "Radial", "#008FFB");
    add(chartYData, "Axial", "#00E396");
    add(chartZData, "Tangential", "#FEB019");

    return series;
  }
}
